import {
    Grid2,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Chip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { deleteCoupon } from '@openapi/coupons/coupons';
import useNotify from '../../../hooks/useNotify';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../helpers/formatDate';
import { useCapabilities } from '../../../hooks/useCapabilities';

const getStatusColor = (status) => {
    switch (status) {
        case 'active':
            return 'success';
        case 'expired':
            return 'error';
        case 'depleted':
            return 'warning';
        default:
            return 'default';
    }
};

const CouponsTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    sort,
    sortDir,
    setSorting,
}) => {
    const { t } = useTranslation();
    const { can, CAPABILITIES } = useCapabilities();
    const canManageCoupons = can(CAPABILITIES.COUPONS_MANAGE);
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();
    const { showId } = useParams();

    const handleDeleteCoupon = useCallback(
        async (id) => {
            setDeletingId(id);
            try {
                await deleteCoupon(id);
                notifySuccess(t('coupons.notifications.deleted'));
                reloadData();
            } catch (error) {
                notifyError(error?.message || t('coupons.notifications.deleteError'));
            } finally {
                setDeletingId(null);
            }
        },
        [notifyError, notifySuccess, reloadData, t],
    );

    const handleCopyCode = useCallback(
        async (code) => {
            try {
                await navigator.clipboard.writeText(code);
                notifySuccess(t('coupons.notifications.codeCopied'));
            } catch {
                notifyError(t('coupons.notifications.copyError'));
            }
        },
        [notifyError, notifySuccess, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer>
                <Table sx={{ minWidth: 720 }}>
                    <TableHead>
                    <TableRow>
                        <SortableTableCell
                            arrow={sort === 'code' ? sortDir : ''}
                            onClick={() => setSorting('code')}
                        >
                            {t('coupons.columns.code')}
                        </SortableTableCell>
                        <SortableTableCell
                            arrow={sort === 'discount_type' ? sortDir : ''}
                            onClick={() => setSorting('discount_type')}
                        >
                            {t('coupons.columns.discountType')}
                        </SortableTableCell>
                        <TableCell>{t('coupons.columns.value')}</TableCell>
                        <SortableTableCell
                            arrow={sort === 'expiration_date' ? sortDir : ''}
                            onClick={() => setSorting('expiration_date')}
                        >
                            {t('coupons.columns.expirationDate')}
                        </SortableTableCell>
                        <TableCell>{t('coupons.columns.status')}</TableCell>
                        <TableCell>{t('coupons.columns.uses')}</TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>
                <TableBody>
                    {loading ? (
                        <SkeletonTable rows={limit} cells={7} />
                    ) : (
                        rows?.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell>
                                    <code style={{ fontWeight: 'bold' }}>{row.code}</code>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopyCode(row.code)}
                                        sx={{ ml: 0.5 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                                <TableCell>
                                    {row.discountType === 'percentage'
                                        ? t('coupons.discountTypes.percentage')
                                        : t('coupons.discountTypes.fixed')}
                                </TableCell>
                                <TableCell>
                                    {row.discountType === 'percentage'
                                        ? `${row.discountValue}%`
                                        : `$${(row.discountValue / 100).toFixed(2)}`}
                                </TableCell>
                                <TableCell>{formatDate(row.expirationDate, 'date')}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={t(`coupons.statuses.${row.status}`)}
                                        color={getStatusColor(row.status)}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>
                                    {row.currentUses} / {row.maxUses || t('coupons.unlimited')}
                                </TableCell>
                                <TableCell align="right">
                                    {canManageCoupons && (
                                        <>
                                            <IconButton
                                                onClick={() =>
                                                    navigate(
                                                        `/shows/${showId}/coupons/new?edit=${row.id}`,
                                                    )
                                                }
                                            >
                                                <Tooltip title={t('common.edit')}>
                                                    <EditIcon />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                onClick={() => {
                                                    showModal(MODAL_TYPE.CONFIRMATION, {
                                                        title: t('confirmationModal.title'),
                                                        content:
                                                            row.currentUses > 0
                                                                ? t(
                                                                      'coupons.deleteUsedConfirmation',
                                                                  )
                                                                : t('confirmationModal.content'),
                                                        confirm: () => handleDeleteCoupon(row.id),
                                                    });
                                                }}
                                                disabled={deletingId === row.id}
                                            >
                                                <Tooltip title={t('common.delete')}>
                                                    <DeleteIcon />
                                                </Tooltip>
                                            </IconButton>
                                        </>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default CouponsTable;
